import { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { AUTOSAVE_DELAY_MS, autosaveAction } from "./autosave";

const WARNING_TEXT =
  "Автоматичното запазване не успя — промените не са запазени.";

/**
 * Wires `autosave.js`'s decision to a debounce timer and the editor's own
 * `save()` (DAB-875).
 *
 * Deliberately not folded into `isBusy`: that flag disables the whole action
 * bar while a manual Save/Publish resolves, and autosave must not do that to
 * a form the editor is still typing into (PageEditor.jsx never disables the
 * fields themselves on `isBusy`, only Back/language/the save buttons). This
 * hook keeps its own in-flight tracking instead, and still avoids ever
 * overlapping a manual save: scheduling is gated on `isBusy` exactly like it
 * is on `isDirty`, and `awaitIdle()` lets the manual handlers wait out an
 * autosave already in flight.
 *
 * @param {object} params
 * @param {boolean} params.isOpen - the live preview pane is open
 * @param {boolean} params.isDirty - the form differs from the saved draft
 * @param {boolean} params.isBusy - a manual Save/Publish is in flight
 * @param {*} params.signal - changes on every edit (the editor's `values`);
 *   used only to re-arm the debounce, never read
 * @param {() => Promise<void>} params.save - the editor's own `save()`
 * @param {*} params.resetKey - changes when the page/language switches;
 *   drops anything pending or in flight for the previous target
 */
export default function useDraftAutosave({
  isOpen,
  isDirty,
  isBusy,
  signal,
  save,
  resetKey,
}) {
  const [isSaving, setIsSaving] = useState(false);

  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  const activePromiseRef = useRef(null);
  // Bumped on every page/language switch so a save that was already in
  // flight cannot act on behalf of a target the editor has since left — the
  // same idea as `loadTokenRef` in PageEditor.jsx.
  const generationRef = useRef(0);

  // Read through refs so the async continuation below always sees the
  // latest `save` and pane state, not the ones captured when it was scheduled.
  const saveRef = useRef(save);
  saveRef.current = save;
  const openRef = useRef(isOpen);
  openRef.current = isOpen;

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const runAutosave = (generation) => {
    timerRef.current = null;
    inFlightRef.current = true;
    pendingRef.current = false;
    setIsSaving(true);
    const promise = saveRef
      .current()
      .catch(() => {
        if (generation !== generationRef.current) return;
        message.warning(WARNING_TEXT);
      })
      .finally(() => {
        inFlightRef.current = false;
        activePromiseRef.current = null;
        if (generation !== generationRef.current) return;
        setIsSaving(false);
        // An edit landed while this save was resolving: exactly one more
        // attempt, still gated on the pane still being open.
        if (pendingRef.current && openRef.current) {
          pendingRef.current = false;
          timerRef.current = setTimeout(
            () => runAutosave(generation),
            AUTOSAVE_DELAY_MS,
          );
        }
      });
    activePromiseRef.current = promise;
  };

  // A page/language switch cancels anything pending for the old target
  // outright, and stops a late response from touching this hook's state.
  useEffect(() => {
    return () => {
      generationRef.current += 1;
      clearTimer();
      inFlightRef.current = false;
      pendingRef.current = false;
      activePromiseRef.current = null;
      setIsSaving(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    clearTimer();
    if (autosaveAction({ isOpen, isDirty }) === "none") {
      pendingRef.current = false;
      return undefined;
    }
    if (isBusy || inFlightRef.current) {
      // A manual save/publish or a previous autosave already owns the draft
      // right now — note the edit and let its own completion (or the next
      // render once `isBusy` clears) schedule the follow-up.
      pendingRef.current = true;
      return undefined;
    }
    pendingRef.current = false;
    const generation = generationRef.current;
    timerRef.current = setTimeout(
      () => runAutosave(generation),
      AUTOSAVE_DELAY_MS,
    );
    return clearTimer;
    // `signal` only re-arms the timer on every edit; its value is never read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isDirty, isBusy, signal]);

  return {
    isSaving,
    /** Manual Save/Publish await this first so they never overlap an autosave. */
    awaitIdle: () => activePromiseRef.current || Promise.resolve(),
  };
}
